/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-07-05 15:25:15
 * @description Implementation of the Contact Us react component
 * @filename contact-us.tsx
 */
import React from "react";
import { gaEvent } from "../../../utilities/general";
import { RdvButton } from "../rdv-button";
import { CreateSupportIssueInput } from "@radivision/graphql/lib/ts/graphql/mutations/create-support-issue-input";
import { MutationController } from "../../../mutation-controller";

/**
 * The props of the Report a Bug component.
 *
 * @interface
 */
interface ContactUsProps {
  environment: any;
}

/**
 * The state of the ContactUs component.
 *
 * @export
 * @interface
 */
interface ContactUsState {
  /**
   * show or hide warning message.
   *
   * @type {string}
   */
  class: string;

  /**
   * A flag which is true if the message length is valid.
   *
   * @type {boolean}
   */
  messageLengthIsValid: boolean;

  /**
   * The value of the Issue description.
   *
   * @type {string}
   */
  value: string;

  /**
   * name of the user reporting the issue.
   *
   * @type {string}
   * @memberof ReportBugState
   */
  name?: string;

  /**
   * mail of the user reporting the issue.
   *
   * @type {string}
   * @memberof ReportBugState
   */
  mail: string;

  /**
   * type of the device the issue appeared in.
   *
   * @type {string}
   * @memberof ReportBugState
   */
  issueDevice?: string;

  /**
   * a boolean which is true if the request is active.
   *
   * @type {boolean}
   * @memberof ReportBugState
   */
  submittingIssue: boolean;

  /**
   *
   *
   * @type {boolean}
   * @memberof ContactUsState
   */
  submittingSuccess?: boolean;

  /**
   * a boolean which is true on sending the message
   * to enable the Proress bar
   */
  loading: boolean;
  /**
   *
   *
   * @type {boolean}
   * @memberof NominatePanelState
   */
  contactUsErrors: { hasError: boolean; errors: string[] };
  /**
   *
   *
   * @type {string}
   * @memberof NominatePanelState
   */
  sucessMessage: string;
}

/**
 *
 * A React component that renders the Contact Us.
 *
 * @extends {React.Component}
 */
export class ContactUs extends React.Component<ContactUsProps, ContactUsState> {
  /**
   * Constructor.
   *
   * @param {ContactUsProps} props The props of the component.
   */
  constructor(props: ContactUsProps) {
    super(props);
    this.onMessageChange = this.onMessageChange.bind(this);
    this.onNameChange = this.onNameChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.contactUs = this.contactUs.bind(this);
    this.state = {
      messageLengthIsValid: false,
      value: "",
      class: "hide",
      mail: null,
      submittingIssue: false,
      loading: false,
      submittingSuccess: false,
      contactUsErrors: { hasError: false, errors: [] },
      sucessMessage: "",
    };
  }

  /**
   * Invked when a change is made to the contact us.
   *
   * @param {React.ChangeEvent<HTMLTextAreaElement>} event The change event.
   */
  onMessageChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    const charactersCount: number = event.target.value.length;
    const target: any = event.target;

    this.setState({
      value: event.target.value,
    });
    if (charactersCount >= 250) {
      this.setState({
        messageLengthIsValid: true,
      });
      target
        .closest(".form-group")
        .querySelector(".invalid-feedback").style.display = "block";
    } else {
      this.setState({
        messageLengthIsValid: false,
      });
      target
        .closest(".form-group")
        .querySelector(".invalid-feedback").style.display = "none";
    }
  }

  /**
   * Invoked when the name value is changed.
   *
   * @param {React.FormEvent<HTMLInputElement>} e The event.
   */
  onNameChange(e: React.FormEvent<HTMLInputElement>) {
    this.setState({ name: e.currentTarget.value });
  }

  /**
   * Invoked when the email value is changed.
   *
   * @param {React.FormEvent<HTMLInputElement>} e The event.
   */
  onEmailChange(e: React.FormEvent<HTMLInputElement>) {
    this.setState({ mail: e.currentTarget.value });
  }

  /**
   * A function to be called when the user needs to report an issue.
   *
   * @memberof ReportBug
   */
  contactUs(e: React.FormEvent): Promise<any> {
    let contactUsInput: CreateSupportIssueInput;
    e.preventDefault();
    this.setState({
      loading: true,
      contactUsErrors: { hasError: false, errors: [] },
      sucessMessage: "",
    });

    if (
      this.state.value.length > 0 &&
      this.state.mail !== null &&
      this.state.mail !== undefined
    ) {
      contactUsInput = {
        clientMutationId: undefined,
        description: this.state.value,
        email: this.state.mail,
      };
      if (this.state.name !== undefined && this.state.name !== null) {
        contactUsInput.name = this.state.name;
      }
      if (
        this.state.issueDevice !== undefined &&
        this.state.issueDevice !== null
      ) {
        contactUsInput.browserAndDeviceType = this.state.issueDevice;
      }

      this.setState({
        submittingIssue: true,
      });

        let promise = MutationController.createSupportIssue(
          contactUsInput
        );
        promise = promise.then((res) => {
          this.setState({
            loading: false,
            submittingIssue: false,
            submittingSuccess: true,
            sucessMessage: "Thank you, your message was submitted successfully",
          });

          gaEvent(
            "Support / ContactUs",
            "Click",
            "Message Send To Support Successfully!",
            true
          );

          let form: any;
          form = document.getElementById("contact-us-form");
          form.reset();

          return Promise.resolve(res);
        });

        promise = promise.catch((err) => {
          let contactUsErrors = this.state.contactUsErrors;
          contactUsErrors = {
            hasError: true,
            errors: ["Unexpected error contact Radivision Technical team."],
          };
          this.setState({
            contactUsErrors,
            loading: false,
            submittingSuccess: true,
          });

          return Promise.reject(err);
        });

        return promise;
    }
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div className="external inner-pages">
        <div className="row alignItems">
          <div className="col-md-8 col-sm-12 col-xs-12 text-center">
            <h1 className="font-weight-bold">Talk to Us!</h1>
            <p className="font-weight-400">We'd love to hear from you!</p>
            <form
              id="contact-us-form"
              onSubmit={this.contactUs}
              className="add-media-modal"
            >
              {this.state.submittingSuccess ? (
                <div>
                  {this.state.contactUsErrors.hasError ? (
                    <div className="alert alert-danger" role="alert">
                      {this.state.contactUsErrors.errors[0]}
                    </div>
                  ) : (
                    <div className="alert alert-success" role="alert">
                      {this.state.sucessMessage}
                    </div>
                  )}
                </div>
              ) : null}

              <div className="form-group">
                <label className="form-group has-float-label"> Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Your full name"
                  onChange={this.onNameChange}
                  disabled={this.state.loading}
                />
              </div>
              <div className="form-group">
                <label className="has-float-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Your email"
                  onChange={this.onEmailChange}
                  required={true}
                  disabled={this.state.loading}
                />
              </div>

              <div className="form-group">
                <label className="has-float-label">Message</label>
                <textarea
                  onChange={this.onMessageChange}
                  placeholder="Your message"
                  className={`form-control ${
                    this.state.messageLengthIsValid ? "is-invalid" : ""
                  }`}
                  maxLength={250}
                  required={true}
                  disabled={this.state.loading}
                />
                <div className="invalid-feedback" style={{ display: "none" }}>
                  The message text should have maximum of 250 characters only!
                </div>
              </div>
              <RdvButton
                type="submit"
                form="contact-us-form"
                text="send message"
                extraClassName="contactButton"
                disabled={this.state.submittingIssue}
                isLoading={this.state.loading}
              />
            </form>
            <p>
              For support or any questions: <br />
              Email us at{" "}
              <a href="mailto:support@radivision.com">support@radivision.com</a>
            </p>
          </div>
          {/* <div className="col-md-2" />
          <div className="col-md-5 right-section">
            <div>
              <h3>Connect with us:</h3>
              <p>
                For support or any questions: Email us at
                <a href="mailto:support@radivision.com">
                  support@radivision.com
                </a>
              </p>
              <p>
                Radivision is headquartered in San Francisco with offices in Los
                Angeles and Cairo.
              </p>
            </div>
          </div> */}
        </div>
      </div>
    );
  }
}
